import React, { useState, useEffect } from 'react';
import { X, Clock, Flame, Calendar, Trash2, Upload, Hand } from 'lucide-react';
import { BakingSession, FERMENTATION_PROCESSES } from '../../types/baking';
import ShapingIcon from '../layout/ShapingIcon';
import { SessionCompletionModal } from '../baking/SessionCompletionModal';
import { deleteBakingSession } from '../../lib/store';

interface SessionDetailsModalProps {
  session: BakingSession | null;
  onClose: () => void;
  onCancel?: (sessionId: string) => Promise<void>;
}

function SessionDetailsModal({ session, onClose, onCancel }: SessionDetailsModalProps) {
  const [showCompletion, setShowCompletion] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset states when modal closes or session changes
  useEffect(() => {
    setIsConfirmingDelete(false);
    setIsDeleting(false);
    setError(null);
  }, [session]);

  if (!session) return null;

  const getKneadingTime = (bakeTime: Date, fermentationType: string) => {
    const process = FERMENTATION_PROCESSES.find(p => p.id === fermentationType);
    if (!process) return bakeTime;
    
    const kneadingTime = new Date(bakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - process.totalTime);
    return kneadingTime;
  };

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleDeleteSession = async () => {
    if (!session.id) return;
    
    try {
      setIsDeleting(true);
      setError(null);
      await deleteBakingSession(session.id);
      onClose();
    } catch (error) {
      console.error('Error deleting session:', error);
      setError('Failed to delete session. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsConfirmingDelete(false); // Reset confirmation state
    }
  };

  const handleCloseModal = () => {
    setIsConfirmingDelete(false); // Reset confirmation state
    setIsDeleting(false);
    setError(null);
    onClose();
  };

  const canDelete = session.status === 'scheduled';
  const canComplete = session.status !== 'cancelled' && session.status !== 'completed';
  const fermentationProcess = FERMENTATION_PROCESSES.find(p => p.id === session.fermentationType);
  const kneadingTime = getKneadingTime(new Date(session.targetBakeTime), session.fermentationType);
  const shapingTime = new Date(session.targetBakeTime);
  shapingTime.setHours(shapingTime.getHours() - (fermentationProcess?.proofTime || 0));
  
  // Calculate timeline data similar to StarterScheduleSection
  const getSessionTimes = () => {
    if (!fermentationProcess) return null;
    
    const bakeTime = new Date(session.targetBakeTime);
    const totalTime = fermentationProcess.totalTime;
    const proofTime = fermentationProcess.proofTime;
    const bulkTime = fermentationProcess.bulkTime;
    
    // Use custom times if available
    let actualTotalTime = totalTime;
    let actualBulkTime = bulkTime;
    let actualProofTime = proofTime;
    
    if (session.fermentationType === 'cold-bulk' && session.customBulkTime) {
      actualTotalTime = session.customBulkTime + proofTime;
      actualBulkTime = session.customBulkTime;
    } else if (session.fermentationType === 'pre-proofed' && session.customProofTime) {
      actualTotalTime = bulkTime + session.customProofTime;
      actualProofTime = session.customProofTime;
    }
    
    const kneadingTime = new Date(bakeTime);
    kneadingTime.setHours(kneadingTime.getHours() - actualTotalTime);
    
    const shapingTime = new Date(bakeTime);
    shapingTime.setHours(shapingTime.getHours() - actualProofTime);
    
    return {
      kneading: kneadingTime,
      shaping: shapingTime,
      baking: bakeTime,
      bulkTime: actualBulkTime,
      proofTime: actualProofTime
    };
  };
  
  const times = getSessionTimes();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg backdrop-blur-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-white font-brand font-light italic">{session.name}</h2>
              <div className="mt-2">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  session.status === 'scheduled' && new Date(session.targetBakeTime).getTime() + 24 * 60 * 60 * 1000 < Date.now()
                  ? 'bg-red-500/20 text-red-200'
                  : session.status === 'scheduled'
                  ? 'bg-blue-500/20 text-blue-200'
                  : session.status === 'in-progress'
                  ? 'bg-green-500/20 text-green-200'
                  : 'bg-white/20 text-white/80'
                }`}>
                  {session.status === 'scheduled' && new Date(session.targetBakeTime).getTime() + 24 * 60 * 60 * 1000 < Date.now()
                  ? 'Over'
                  : session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                </div>
              </div>
            </div>
            <button 
              onClick={handleCloseModal}
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Timing Section */}
          {/* Session Timeline */}
          {times && (
            <div className="space-y-4">
              <h3 className="font-medium text-white mb-4 ">Session Timeline</h3>
              
              <div className="relative space-y-4">
                <div className="relative flex items-center gap-4 p-4 bg-blue-500/10 border border-white/20 hover:border hover:border-blue-500 rounded-xl z-10">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500/50 rounded-full flex items-center justify-center">
                    <Hand className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Kneading</h4>
                    <p className="text-sm text-white/70">
                      {formatDateTime(times.kneading)}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-white mt-1 ml-20">
                  <Clock className="w-3 h-3 inline mr-2" />
                  +{times.bulkTime}h (Bulk fermentation)
                </p>
                
                <div className="relative flex items-center gap-4 p-4 bg-green-500/10 border border-white/20 hover:border hover:border-green-500 rounded-xl z-10">
                  {/* Line connecting to previous step */}
                  <div className="absolute left-10 -top-14 w-1 h-14 bg-white/60 z-0"></div>
                  <div className="flex-shrink-0 w-12 h-12 bg-green-500/50 rounded-full flex items-center justify-center">
                    <ShapingIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Shaping</h4>
                    <p className="text-sm text-white/70">
                      {formatDateTime(times.shaping)}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-white mt-1 ml-20">
                  <Clock className="w-3 h-3 inline mr-2" />
                  +{times.proofTime}h (Proofing)
                </p>
                
                <div className="relative flex items-center gap-4 p-4 bg-orange-500/10 border border-white/20 hover:border hover:border-orange-500 rounded-xl z-10">
                  {/* Line connecting to previous step */}
                  <div className="absolute left-10 -top-14 w-1 h-14 bg-white/60 z-0"></div>
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-500/50 rounded-full flex items-center justify-center">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white">Baking</h4>
                    <p className="text-sm text-white/70">
                      {formatDateTime(times.baking)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
            {session.starterSchedule?.feeds && !session.starterSchedule.feeds[0]?.noFeedsNeeded && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-white/90 mb-3">Starter Schedule</h4>
                <div className="space-y-3">
                  {session.starterSchedule.feeds.map((feed, index) => (
                    <div key={feed.id} className="flex items-center justify-between p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                      <div>
                        <p className="font-medium text-white">Feed #{index + 1}</p>
                        <p className="text-sm text-white/70">
                          {formatDateTime(new Date(feed.scheduledTime))}
                        </p>
                      </div>
                      {feed.completed && (
                        <span className="text-sm font-medium text-green-200 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                          Completed
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Fermentation Section */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-white">
              <Calendar className="w-5 h-5 text-amber-400" />
              Fermentation Process
            </h3>
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="font-medium text-white">{fermentationProcess?.name}</p>
              <p className="text-sm text-white/70 mt-1">{fermentationProcess?.description}</p>
              <div className="mt-3 text-sm text-white/80">
                <p>Total time: {fermentationProcess?.totalTime}h</p>
                <p>Starter percentage: {fermentationProcess?.starterPercentage}</p>
              </div>
            </div>
          </div>

          {/* Oven Section */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2 text-white">
              <Flame className="w-5 h-5 text-amber-400" />
              Oven Setup
            </h3>
            <div className="p-4 bg-white/10 rounded-xl backdrop-blur-sm">
              <p className="font-medium text-white">
                {session.ovenType === 'domestic' ? 'Domestic Oven' : 'Wood-Fired Oven'}
              </p>
              {session.woodOvenDetails && (
                <div className="mt-2 text-sm text-white/70">
                  <p>Size: {session.woodOvenDetails.size}</p>
                  <p>Last used: {new Date(session.woodOvenDetails.lastUsed).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>
        </div>
          <div className="p-6 border-t border-white/10 bg-white/5">
          <div className="flex justify-between items-center">
            {/* Delete button */}
            {!showCompletion && !isConfirmingDelete && canDelete && (
              <button
                onClick={() => setIsConfirmingDelete(true)}
                className="flex items-center gap-2 px-6 py-2 bg-red-500/20 text-red-200 rounded-xl hover:bg-red-500/30 transition-all duration-300"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}

            {/* Delete confirmation */}
            {isConfirmingDelete ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsConfirmingDelete(false)}
                  className="px-6 py-2 border border-white/20 rounded-xl hover:bg-white/10 transition-colors"
                  disabled={isDeleting}
                >
                  Keep Session
                </button>
                <button
                  onClick={handleDeleteSession}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-6 py-2 bg-red-500/90 hover:bg-red-500 text-white rounded-xl transition-all duration-300 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            ) : (
              /* Complete button */
              canComplete && (
                <button
                  onClick={() => setShowCompletion(true)}
                  className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-500 hover:to-amber-600 text-white rounded-xl disabled:opacity-50 transition-all duration-300"
                >
                  <Upload className="w-4 h-4" />
                  Complete
                </button>
              )
            )}
          </div>
        </div>
      </div>

        
        <SessionCompletionModal
          session={session}
          isOpen={showCompletion}
          onClose={() => setShowCompletion(false)}
          onComplete={handleCloseModal}
        />
    </div>
  );
}

export { SessionDetailsModal };